import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import {
  Button,
  Card,
  EditIcon,
  ForumIcon,
  RobotIcon,
  Tag,
  Text,
  Skeleton,
} from '@mercai/clever'

import {
  ConversationDrawerChat,
  ConversationDrawerShow,
} from '@/pages/ai-center/conversations/components'

import styles from './ai-qualification-card.module.css'

import type { AiQualificationCardProps } from './ai-qualification-card.type'

const AiQualificationCardCore = ({
  aiQualificationData,
}: AiQualificationCardProps) => {
  const { t } = useTranslation('componentsAiQualificationCard')

  const [isOpenChat, setIsOpenChat] = useState(false)
  const [isOpenShow, setIsOpenShow] = useState(false)

  if (!aiQualificationData?.finalScoring) {
    return null
  }

  const calificationBadge = {
    qualified: (
      <Tag variant="info" model="light" size="small">
        {t('aiQualificationCardQualifiedByAI')}

        <RobotIcon />
      </Tag>
    ),
    disqualified: (
      <Tag variant="danger" model="light" size="small">
        {t('aiQualificationCardNotQualifiedByAI')}

        <RobotIcon />
      </Tag>
    ),
    pending: (
      <Tag variant="warning" model="light" size="small">
        {t('aiQualificationCardPending')}
      </Tag>
    ),
  }

  const getCalificationBadge = () => {
    if (
      (aiQualificationData.finalScoring >= 6.5 &&
        aiQualificationData.isQualified === undefined) ||
      aiQualificationData.isQualified
    ) {
      return calificationBadge.qualified
    }

    if (
      (aiQualificationData.finalScoring < 6.5 &&
        aiQualificationData.isQualified === undefined) ||
      !aiQualificationData.isQualified
    ) {
      return calificationBadge.disqualified
    }

    return calificationBadge.pending
  }

  return (
    <>
      <Card className={styles.core}>
        <img
          src="/images/ai-circle.png"
          alt="AI Circle"
          className={styles['ai-circle']}
        />

        <div className={styles.content}>
          <div className={styles.tags}>
            {getCalificationBadge()}

            {aiQualificationData.finalScoring && (
              <Tag
                variant={
                  aiQualificationData.finalScoring >= 6.5 ? 'success' : 'danger'
                }
                model="light"
                size="small"
              >
                {t('aiQualificationCardNote{{scoring}}{{total}}', {
                  scoring: aiQualificationData.finalScoring,
                  total: aiQualificationData.finalScoring > 10 ? 100 : 10,
                })}
              </Tag>
            )}
          </div>

          <Text variant="text-small">{aiQualificationData.recommendation}</Text>
        </div>

        <div className={styles.buttons}>
          <Button
            variant="secondary"
            model="round"
            onClick={() => setIsOpenChat(true)}
          >
            <ForumIcon />
          </Button>

          <Button
            variant="primary"
            model="round"
            onClick={() => setIsOpenShow(true)}
          >
            <EditIcon />
          </Button>
        </div>
      </Card>

      <ConversationDrawerChat
        isOpen={isOpenChat}
        uuid={aiQualificationData?.externalConversationUuid}
        onClose={() => setIsOpenChat(false)}
      />

      <ConversationDrawerShow
        isOpen={isOpenShow}
        uuid={aiQualificationData?.externalConversationUuid}
        onHandleOpen={() => setIsOpenShow(false)}
        variation="acquisition"
      />
    </>
  )
}

const AiQualificationCardSkeleton = () => {
  return (
    <div className={styles.core}>
      <Skeleton className={styles['ai-circle']} />

      <div className={styles.content}>
        <div className={styles.tags}>
          <Skeleton width="6rem" height="1.15rem" />
          <Skeleton width="8rem" height="1.15rem" />
        </div>

        <Skeleton width="26rem" height="1rem" />
      </div>

      <div className={styles.buttons}>
        <Skeleton width="2.5rem" height="2.5rem" isCircle />
        <Skeleton width="2.5rem" height="2.5rem" isCircle />
      </div>
    </div>
  )
}

export const AiQualificationCard = ({
  aiQualificationData,
  isLoading,
}: AiQualificationCardProps) => {
  if (isLoading) {
    return <AiQualificationCardSkeleton />
  }

  return <AiQualificationCardCore aiQualificationData={aiQualificationData} />
}
